import { useEffect, useState } from "react";
import { Box } from "@mui/material";

import TableTopBar from "../component/TableTopBar";
import TableProductPartnerResult from "../component/TableProductPartnerResult";
import SerchSideBar from "../component/SerchSideBar";

import { useSetAtom, useAtomValue } from "jotai";
import { AppAtoms } from "../lib/store";

import {
  getProductPartnerResult,
  ProductPartnerResultResponse,
} from "../api/productPartnerResult";
import {
  getSerchSaleCompanys,
  getSerchHighRankCompanys,
  getSerchProducts,
} from "../api/serch";
import { productPartnerResultTableColumn } from "../config/productPartnerResult";
import { getFirstYear, getOneYear } from "../lib/other";

type SaleCompany = { id: number; sale_company_name: string };
type HighRankCompany = { id: number; high_rank_company_name: string };
type Product = { id: number; product_name: string };

function getInitialMonthRange() {
  const now = new Date();
  const endYear = now.getFullYear();
  const endMonth = now.getMonth() + 1;
  let startYear = endYear;
  let startMonth = endMonth - 5;
  if (startMonth <= 0) {
    startMonth += 12;
    startYear -= 1;
  }
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    start: `${startYear}-${pad(startMonth)}`,
    end: `${endYear}-${pad(endMonth)}`,
  };
}

function getCurrentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function ProductPartnerResult() {
  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);
  const setIsLoading = useSetAtom(AppAtoms.isLoading);

  const [serchOpen, setSerchOpen] = useState(true);
  const [displayMode, setDisplayMode] = useState<"月別" | "日別">("月別");
  const [saleCompanys, setSaleCompanys] = useState<SaleCompany[]>([]);
  const [highRankCompanys, setHighRankCompanys] = useState<HighRankCompany[]>(
    [],
  );
  const [products, setProducts] = useState<Product[]>([]);
  const [data, setData] = useState<ProductPartnerResultResponse | null>(null);
  const [displayNumber, setDisplayNumber] = useState(25);
  const [pageNumber, setPageNumber] = useState(1);
  const DISPLAY_NUMBERS = [10, 25, 50];

  const firstYear = getFirstYear();
  const { thisYear } = getOneYear();

  const initialRange = getInitialMonthRange();
  const serchColumnDef = {
    start_month: initialRange.start,
    end_month: initialRange.end,
    year_month: getCurrentMonth(),
    sale_company_name: "",
    high_rank_company_name: "",
    product_name: "",
  };
  const [serchString, setSerchString] = useState<{ [key: string]: string }>(
    serchColumnDef,
  );
  const [serchStringView, setSerchStringView] = useState<{
    [key: string]: string;
  }>(serchColumnDef);

  const [tableHeight, setTableHeight] = useState("calc(100vh - 110px)");
  useEffect(() => {
    setTableHeight(
      isSmartPhone ? "calc(100vh - 190px)" : "calc(100vh - 110px)",
    );
  }, [isSmartPhone]);

  // パートナー一覧取得
  useEffect(() => {
    (async () => {
      const result = await getSerchSaleCompanys();
      if (result?.saleCompanys) {
        setSaleCompanys(result.saleCompanys as SaleCompany[]);
      }
    })();
  }, []);

  // 上位店・商材一覧取得
  useEffect(() => {
    (async () => {
      const hrcResult = await getSerchHighRankCompanys();
      if (hrcResult?.highRankCompanys) {
        setHighRankCompanys(hrcResult.highRankCompanys as HighRankCompany[]);
      }
      const productResult = await getSerchProducts();
      if (productResult?.products) {
        setProducts(productResult.products as Product[]);
      }
    })();
  }, []);

  // serchString または displayMode 変更時にAPIを呼び出す
  useEffect(() => {
    fetchResult(undefined, 1);
    setPageNumber(1);
  }, [serchString, displayMode]);

  const fetchResult = async (
    overrideDisplayNumber?: number,
    overridePage?: number,
  ) => {
    const dn =
      overrideDisplayNumber !== undefined
        ? overrideDisplayNumber
        : displayNumber;
    const pg = overridePage !== undefined ? overridePage : pageNumber;
    setIsLoading(true);
    // sale_company_name(カンマ区切り名前) → partner_ids(ID)に変換
    const partnerNames = serchString.sale_company_name
      ? serchString.sale_company_name
          .split(",")
          .map((n) => n.trim())
          .filter(Boolean)
      : [];
    const partnerIds = saleCompanys
      .filter((c) => partnerNames.includes(c.sale_company_name))
      .map((c) => c.id);

    const params: Parameters<typeof getProductPartnerResult>[0] = {
      mode: displayMode === "月別" ? "monthly" : "daily",
    };
    if (displayMode === "月別") {
      params.start_month = serchString.start_month;
      params.end_month = serchString.end_month;
    } else {
      params.year_month = serchString.year_month;
    }
    if (partnerIds.length > 0) {
      params.partner_ids = partnerIds.join(",");
    }

    const hrcNames = serchString.high_rank_company_name
      ? serchString.high_rank_company_name
          .split(",")
          .map((n) => n.trim())
          .filter(Boolean)
      : [];
    const hrcIds = highRankCompanys
      .filter((c) => hrcNames.includes(c.high_rank_company_name))
      .map((c) => c.id);
    if (hrcIds.length > 0) {
      params.high_rank_company_ids = hrcIds.join(",");
    }

    const productNameFilters = serchString.product_name
      ? serchString.product_name
          .split(",")
          .map((n) => n.trim())
          .filter(Boolean)
      : [];
    const productFilterIds = products
      .filter((p) => productNameFilters.includes(p.product_name))
      .map((p) => p.id);
    if (productFilterIds.length > 0) {
      params.product_ids = productFilterIds.join(",");
    }

    params.displayNumber = String(dn);
    params.pageNumber = String(pg);
    const result = await getProductPartnerResult(params);
    setData(result);
    setIsLoading(false);
  };

  const handleSetSerchOpen = (open: boolean) => {
    setSerchOpen(open);
  };

  const handleSetDisplayMode = (mode: "日別 / 月別" | "日別" | "月別") => {
    setDisplayMode(mode as "日別" | "月別");
  };

  const totalProductCount = data?.count_data ?? 0;
  const maxPageNumber = Math.max(
    1,
    Math.ceil(totalProductCount / displayNumber),
  );

  return (
    <Box
      style={{
        width: isSmartPhone
          ? "calc(100% - 31px)"
          : serchOpen
            ? "calc(100% - 331px)"
            : "calc(100% - 31px)",
        height: isSmartPhone ? "calc(100vh - 120px)" : "calc(100vh - 40px)",
      }}
    >
      <TableTopBar
        serch={true}
        serchOpen={serchOpen}
        setSerchOpen={handleSetSerchOpen}
        addDataNames={[]}
        addDataName=""
        setAddDataName={() => {}}
        addCSVDataNames={[]}
        addCSVDataName=""
        setAddCSVDataName={() => {}}
        getXLSXDataNames={[]}
        getXLSXDataName=""
        setGetXLSXDataName={() => {}}
        editDatasNames={[]}
        editDatasName=""
        setEditDatasName={() => {}}
        editIDs={[]}
        columnSettingOpen={false}
        openColumnSettingWindow={() => {}}
      />
      <Box style={{ display: "flex" }}>
        {serchOpen && isSmartPhone ? null : (
          <Box
            style={{
              width: isSmartPhone ? undefined : "100%",
              margin: isSmartPhone ? undefined : "10px 15px",
              height: isSmartPhone ? undefined : tableHeight,
              maxHeight: isSmartPhone ? undefined : tableHeight,
            }}
          >
            <TableProductPartnerResult
              data={data}
              displayMode={displayMode}
              serchOpen={serchOpen}
              tableHeight={tableHeight}
              totalProductCount={totalProductCount}
              displayNumber={displayNumber}
              setDisplayNumber={setDisplayNumber}
              displayNumbers={DISPLAY_NUMBERS}
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              maxPageNumber={maxPageNumber}
              fetchResult={fetchResult}
            />
          </Box>
        )}
        {serchOpen && (
          <SerchSideBar
            setSerchOpen={handleSetSerchOpen}
            tableHeight={tableHeight}
            tableColumn={productPartnerResultTableColumn}
            dataColumn={[]}
            firstYear={firstYear}
            thisYear={thisYear}
            serchColumn={serchColumnDef}
            setSerchString={setSerchString}
            companyNameList={saleCompanys.map((c) => c.sale_company_name)}
            companyGroupNameList={[]}
            highRankCompanyNameList={highRankCompanys.map(
              (c) => c.high_rank_company_name,
            )}
            categoryNameList={[]}
            productNameList={products.map((p) => p.product_name)}
            accountSaleNameList={[]}
            accountStaffNameList={[]}
            serchStringView={serchStringView}
            setSerchStringView={setSerchStringView}
            displayMode={displayMode}
            setDisplayMode={handleSetDisplayMode}
          />
        )}
      </Box>
    </Box>
  );
}

export default ProductPartnerResult;
